/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, ReactNode } from "react";
import { type DBPool } from "../duckdb.ts";
import { type Timeseries } from "../types";

import { useControl } from "../control/ControlContext";
import { useDatabase } from "../database/DatabaseContext";
import { DataContext, DataContextType } from "./DataContext";
import { BoxplotData, ChartData, PreparedData } from "../config/data.ts";
import { FilterField, filterWhereClause } from "../config/filter.ts";
import { CONTINUOUS_MEASURES, DISCRETE_MEASURES } from "../config/metrics.ts";
import { DIMENSIONS } from "../config/dimensions.ts";
import { FORCED_MEASURE_GRANULARITY, PLOTS, type TimeGrain } from "../config/plots.ts";

interface DataProviderProps {
  children: ReactNode;
}

export function DataProvider({ children }: DataProviderProps) {
  const { dbPool } = useDatabase();
  const { filterField, filterValue, timeRange } = useControl();

  const [isLoading, setIsLoading] = useState(false);
  const [preparedData, setPreparedData] = useState<PreparedData | undefined>(undefined);

  useEffect(() => {
    if (!dbPool) return;
    if (!timeRange.start || !timeRange.end) return;
    const diffInDays = (timeRange.end.getTime() - timeRange.start.getTime()) / (1000 * 60 * 60 * 24);
    const boxplotGrain: TimeGrain = diffInDays > 63 ? "week" : "day";
    setIsLoading(true);
    prepareData(dbPool, filterField, filterValue, { start: timeRange.start, end: timeRange.end }, boxplotGrain)
      .then(setPreparedData)
      .finally(() => setIsLoading(false));
  }, [dbPool, filterField, filterValue, timeRange]);

  const contextValue: DataContextType = {
    preparedData,
    isLoading,
  };

  return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
}

async function prepareData(
  dbPool: DBPool,
  filterField: FilterField,
  filterValue: string,
  timeRange: { start: Date; end: Date },
  boxplotGrain: TimeGrain,
): Promise<PreparedData> {
  const startTime = performance.now();
  const preparedData: PreparedData = {};

  const discreteMetrics = DISCRETE_MEASURES;
  const continuousMetrics = CONTINUOUS_MEASURES;
  const timeWhere = buildTimeWhereClause(timeRange);

  for (const metric of discreteMetrics) {
    for (const dimension of DIMENSIONS) {
      const filterWhere = filterWhereClause(filterField, filterValue, dimension.id);

      let data: ChartData = {};

      try {
        if (dimension.id === "all") {
          const sql = `SELECT bid_date AS ts, ${metric.sql} AS val FROM aggregated_data WHERE group_type='${dimension.id}' ${filterWhere} ${timeWhere} ORDER BY bid_date`;
          data = await duckDBTimeseries(dbPool, sql);
        } else {
          const dim = dimension.dim2Field != null ? "dim_val1 || '|' || dim_val2" : "dim_val1";
          const sql = `PIVOT (
            SELECT bid_date AS ts, ${dim} AS dim, ${metric.sql} AS val
            FROM aggregated_data
            WHERE group_type='${dimension.id}' ${filterWhere} ${timeWhere}
          ) ON dim USING MAX(val) ORDER BY ts;`;
          data = await duckDBDimensionSeries(dbPool, sql);
        }
      } catch (e) {
        console.warn(`[DataProvider] Skipping metric ${metric.id} / ${dimension.id}:`, e);
      }

      if (!preparedData[metric.id]) preparedData[metric.id] = {};
      preparedData[metric.id][dimension.id] = data;
    }
  }

  for (const metric of continuousMetrics) {
    for (const dimension of DIMENSIONS) {
      const filterWhere = filterWhereClause(filterField, filterValue, dimension.id);
      const meanExpr = metric.meanExpr ?? `${metric.sql}_mean`;
      const stdExpr = metric.stdExpr ?? `${metric.sql}_sd`;
      const normalizeExpr = metric.normalizeExpr ?? metric.normalizeBy;
      if (!normalizeExpr) throw new Error(`Missing normalizeExpr for metric ${metric.id}`);

      let data: ChartData;
      try {
        if (dimension.id === "all") {
          const sql = `SELECT bid_date AS ts, ${meanExpr} AS mean, ${stdExpr} AS std, ${normalizeExpr} AS normalizeBy FROM aggregated_data WHERE group_type='${dimension.id}' ${filterWhere} ${timeWhere} ORDER BY bid_date`;
          data = await duckDBContinuous(dbPool, sql);
        } else {
          const dimValue = dimension.dim2Field != null ? "dim_val1 || '|' || dim_val2" : "dim_val1";
          const sql = `PIVOT (
            SELECT bid_date AS ts, ${dimValue} AS dim, ${meanExpr} AS val
            FROM aggregated_data
            WHERE group_type='${dimension.id}' ${filterWhere} ${timeWhere}
          ) ON dim USING MAX(val) ORDER BY ts;`;
          data = await duckDBDimensionSeries(dbPool, sql);
        }
      } catch (e) {
        console.warn(`[DataProvider] Skipping metric ${metric.id} / ${dimension.id}:`, e);
        data = {};
      }

      if (!preparedData[metric.id]) preparedData[metric.id] = {};
      preparedData[metric.id][dimension.id] = data;
    }
  }

  // Handle boxplot metrics - they aggregate across a dimension to show distribution
  // Boxplots use auto-detected grain from timeRange; they don't respond to the grain toggle.
  const boxplotPlots = PLOTS.filter(p => p.chart === "boxplot" && p.boxplot);
  for (const plot of boxplotPlots) {
    const boxplotConfig = plot.boxplot!;
    const metric = [...discreteMetrics, ...continuousMetrics].find(m => m.id === plot.measureId);
    if (!metric) continue;

    // Boxplots use boxplotGrain (respects the grain toggle) — forced-daily metrics (unique users)
    // use AVG instead of SUM when bucketing to weekly, since unique counts can't be summed across days
    const isForcedDaily = FORCED_MEASURE_GRANULARITY[metric.id] === "day";

    const lowerPct = boxplotConfig.lowerExtreme ?? 0.1;
    const upperPct = boxplotConfig.upperExtreme ?? 0.9;

    const filterWhere = filterWhereClause(filterField, filterValue, boxplotConfig.aggregateDimension);
    const valueExpr = metric.meanExpr ?? metric.sql;
    const agg = metric.isCumulative && !isForcedDaily ? "SUM" : "AVG";

    // For weekly granularity, bucket dates to week start (Monday)
    const dateExpr = boxplotGrain === "week" ? "date_trunc('week', bid_date)" : "bid_date";

    // Subquery to filter dimensions with non-zero/non-null values
    const nonZeroDimsSubquery = `
      SELECT dim_val1
      FROM aggregated_data
      WHERE group_type='${boxplotConfig.aggregateDimension}'
        AND ${valueExpr} IS NOT NULL
        ${filterWhere} ${timeWhere}
      GROUP BY dim_val1
      HAVING COALESCE(${agg}(${valueExpr}), 0) > 0
    `;

    const dimensionValuesSql = `
      SELECT
        dim_val1 AS name,
        ${agg}(${valueExpr}) AS value
      FROM aggregated_data
      WHERE group_type='${boxplotConfig.aggregateDimension}'
        AND ${valueExpr} IS NOT NULL
        ${filterWhere} ${timeWhere}
      GROUP BY dim_val1
      HAVING COALESCE(${agg}(${valueExpr}), 0) > 0
      ORDER BY value DESC
    `;

    // For weekly granularity, include day count to filter incomplete weeks
    const dimensionValuesByDateSql =
      boxplotGrain === "week"
        ? `
      SELECT
        ${dateExpr} AS ts,
        dim_val1 AS name,
        ${agg}(${valueExpr}) AS value,
        COUNT(DISTINCT bid_date) AS day_count
      FROM aggregated_data
      WHERE group_type='${boxplotConfig.aggregateDimension}'
        AND ${valueExpr} IS NOT NULL
        AND dim_val1 IN (${nonZeroDimsSubquery})
        ${filterWhere} ${timeWhere}
      GROUP BY ${dateExpr}, dim_val1
      ORDER BY ${dateExpr}, value DESC
    `
        : `
      SELECT
        ${dateExpr} AS ts,
        dim_val1 AS name,
        ${agg}(${valueExpr}) AS value
      FROM aggregated_data
      WHERE group_type='${boxplotConfig.aggregateDimension}'
        AND ${valueExpr} IS NOT NULL
        AND dim_val1 IN (${nonZeroDimsSubquery})
        ${filterWhere} ${timeWhere}
      GROUP BY ${dateExpr}, dim_val1
      ORDER BY ${dateExpr}, value DESC
    `;

    const extremeLabels = {
      lower: `${(lowerPct * 100).toFixed(0)}%`,
      upper: `${(upperPct * 100).toFixed(0)}%`,
    };

    try {
      const data = await duckDBBoxplot(
        dbPool,
        dimensionValuesSql,
        dimensionValuesByDateSql,
        extremeLabels,
        { lower: lowerPct, upper: upperPct },
        boxplotConfig.logScale,
        boxplotGrain,
      );

      // Store under plot.id to support multiple boxplots for the same metric
      if (!preparedData[metric.id]) {
        preparedData[metric.id] = {};
      }
      preparedData[metric.id][plot.id as any] = data;
    } catch (e) {
      console.log(`[DataProvider] Boxplot data not available for ${plot.id}:`, e);
    }
  }

  const endTime = performance.now();
  console.log(`[DataProvider] prepareData: ${(endTime - startTime).toFixed(2)} ms`);
  return preparedData;
}

async function duckDBTimeseries(dbPool: DBPool, sql: string): Promise<Timeseries> {
  const db = dbPool.getDB();
  const conn = await db.connect();
  try {
    const stmt = await conn.prepare(sql);
    const result = await stmt.query();
    const rows = result.toArray();
    const timestamps: Date[] = rows.map(r => new Date(r.ts));
    const data: any = rows.map(r => (r.val != null ? Number(r.val) : null));
    await stmt.close();
    return { timestamps, data };
  } finally {
    await conn.close();
  }
}

async function duckDBDimensionSeries(dbPool: DBPool, sql: string): Promise<{ [val: string]: Timeseries }> {
  const db = dbPool.getDB();
  const conn = await db.connect();
  try {
    const result = await conn.query(sql);
    const rows = result.toArray().map(row => row.toJSON());
    if (rows.length === 0) {
      console.log(`[duckDBDimensionSeries] No data for sql: ${sql}`);
      return {};
    }
    const dimensions = Object.keys(rows[0]).filter(col => col !== "ts");

    const series: { [key: string]: Timeseries } = {};
    for (const dim of dimensions) {
      series[dim] = {
        timestamps: rows.map(row => new Date(row.ts)),
        data: rows.map(row => (row[dim] != null ? Number(row[dim]) : null)) as any,
      };
    }

    return series;
  } finally {
    await conn.close();
  }
}

async function duckDBContinuous(dbPool: DBPool, sql: string): Promise<ContinuousData> {
  const db = dbPool.getDB();
  const conn = await db.connect();
  try {
    const result = await conn.query(sql);

    const rows = result.toArray().map(row => row.toJSON());
    const series: ContinuousData = {
      mean: {
        timestamps: rows.map(row => new Date(row.ts)),
        data: rows.map(row => (row.mean != null ? Number(row.mean) : null)) as any,
      },
      std: {
        timestamps: rows.map(row => new Date(row.ts)),
        data: rows.map(row => (row.std != null ? Number(row.std) : null)) as any,
      },
      normalizeBy: {
        timestamps: rows.map(row => new Date(row.ts)),
        data: rows.map(row => (row.normalizeBy != null ? Number(row.normalizeBy) : null)) as any,
      },
    };

    return series;
  } finally {
    await conn.close();
  }
}

// Calculate percentile from sorted array using linear interpolation (same as SQL percentile_cont)
function percentile(sortedValues: number[], p: number): number {
  if (sortedValues.length === 0) return 0;
  if (sortedValues.length === 1) return sortedValues[0];

  const index = p * (sortedValues.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  if (lower === upper) return sortedValues[lower];
  return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
}

async function duckDBBoxplot(
  dbPool: DBPool,
  dimensionValuesSql: string,
  dimensionValuesByDateSql: string,
  extremeLabels: { lower: string; upper: string },
  percentileThresholds: { lower: number; upper: number },
  logScale?: boolean,
  grain?: TimeGrain,
): Promise<BoxplotData> {
  const db = dbPool.getDB();
  const conn = await db.connect();
  try {
    // Fetch per-dimension values for "View Data" table (aggregate)
    const dimResult = await conn.query(dimensionValuesSql);
    const dimRows = dimResult.toArray().map(row => row.toJSON());
    const dimensionValues = dimRows.map(row => ({
      name: String(row.name),
      value: Number(row.value),
    }));

    // Fetch per-date, per-dimension values for drill-down and chart percentiles
    const dimByDateResult = await conn.query(dimensionValuesByDateSql);
    const dimByDateRows = dimByDateResult.toArray().map(row => row.toJSON());

    // For weekly grain, track day counts per week to filter incomplete weeks
    const weekDayCounts: Record<string, number> = {};
    const dimensionValuesByDate: Record<string, { name: string; value: number }[]> = {};

    for (const row of dimByDateRows) {
      const dateKey = new Date(row.ts).toISOString().split("T")[0];
      if (!dimensionValuesByDate[dateKey]) {
        dimensionValuesByDate[dateKey] = [];
      }
      dimensionValuesByDate[dateKey].push({
        name: String(row.name),
        value: Number(row.value),
      });

      // Track max day_count per week (all dimensions in a week should have same day_count)
      if (grain === "week" && row.day_count != null) {
        const dayCount = Number(row.day_count);
        weekDayCounts[dateKey] = Math.max(weekDayCounts[dateKey] ?? 0, dayCount);
      }
    }

    // Calculate percentiles in JS from dimensionValuesByDate (same data as table uses)
    const dates = Object.keys(dimensionValuesByDate).sort();
    const timestamps: Date[] = [];
    const boxplotValues: [number, number, number, number, number][] = [];
    const filteredDimensionValuesByDate: Record<string, { name: string; value: number }[]> = {};

    for (const dateKey of dates) {
      // Filter incomplete weeks (require 7 days of data)
      if (grain === "week" && weekDayCounts[dateKey] !== 7) {
        continue;
      }

      const values = dimensionValuesByDate[dateKey].map(d => d.value).sort((a, b) => a - b);
      if (values.length === 0) continue;

      timestamps.push(new Date(dateKey));
      boxplotValues.push([
        percentile(values, percentileThresholds.lower),
        percentile(values, 0.25),
        percentile(values, 0.5),
        percentile(values, 0.75),
        percentile(values, percentileThresholds.upper),
      ]);
      filteredDimensionValuesByDate[dateKey] = dimensionValuesByDate[dateKey];
    }

    return {
      timestamps,
      boxplotValues,
      extremeLabels,
      logScale,
      dimensionValues,
      dimensionValuesByDate: filteredDimensionValuesByDate,
    };
  } finally {
    await conn.close();
  }
}

function buildTimeWhereClause(range: { start: Date; end: Date }, dateColumn: string = "bid_date"): string {
  const start = range.start.toISOString().split("T")[0];
  const end = range.end.toISOString().split("T")[0];
  return `AND ${dateColumn} >= DATE '${start}' AND ${dateColumn} <= DATE '${end}'`;
}
